import Papa from 'papaparse';
import type { Category, ImportRow, Resolution, Status } from './api';
// Resolution kept for future header-mapping enhancements
export type { Resolution };

// Mapping từ header CSV (lowercased) → field name trong ImportRow
const HEADER_MAP: Record<string, keyof ImportRow> = {
  // SKU / id
  sku: 'id',
  'sku / mã sp': 'id',
  'mã sp': 'id',
  'mã sản phẩm': 'id',
  id: 'id',
  // Name
  'tên sản phẩm': 'name',
  name: 'name',
  tên: 'name',
  product: 'name',
  // Brand
  hãng: 'brand',
  brand: 'brand',
  // Category
  category: 'category',
  loại: 'category',
  'phân khúc': 'category',
  // Size
  'kích thước': 'size',
  size: 'size',
  inch: 'size',
  // Resolution
  'độ pg': 'resolution',
  'độ phân giải': 'resolution',
  resolution: 'resolution',
  // Refresh rate
  'tần số quét': 'refresh_rate',
  hz: 'refresh_rate',
  refresh: 'refresh_rate',
  refresh_rate: 'refresh_rate',
  // Panel
  panel: 'panel_type',
  'tấm nền': 'panel_type',
  panel_type: 'panel_type',
  // Tags
  tags: 'tags',
  // Prices
  msrp: 'price_msrp',
  'giá msrp': 'price_msrp',
  price_msrp: 'price_msrp',
  'giá ct': 'price_ct',
  price_ct: 'price_ct',
  'giá web': 'price_web',
  price_web: 'price_web',
  '% giảm': 'discount_pct',
  discount: 'discount_pct',
  discount_pct: 'discount_pct',
  'ln còn lại': 'margin_vnd',
  'lợi nhuận': 'margin_vnd',
  margin: 'margin_vnd',
  margin_vnd: 'margin_vnd',
  '% ln': 'margin_pct',
  '% lợi nhuận': 'margin_pct',
  margin_pct: 'margin_pct',
  npp: 'npp',
  'nhà phân phối': 'npp',
  // Flags
  is_focal: 'is_focal',
  focal: 'is_focal',
  is_exclusive: 'is_exclusive',
  exclusive: 'is_exclusive',
  'độc quyền': 'is_exclusive',
  is_oled_focus: 'is_oled_focus',
  oled_focus: 'is_oled_focus',
  is_branding: 'is_branding',
  branding: 'is_branding',
  // Status
  status: 'status',
  'trạng thái': 'status',
  // Node / notes
  node: 'node_raw',
  'ghi chú': 'node_raw',
  node_raw: 'node_raw',
  thumb_url: 'thumb_url',
};

const BRAND_ALIASES: Record<string, string> = {
  lg: 'LG',
  asus: 'ASUS',
  'asus rog': 'ASUS',
  'asus tuf': 'ASUS',
  'asus proart': 'ASUS',
  samsung: 'Samsung',
  msi: 'MSI',
  gigabyte: 'Gigabyte',
  aoc: 'AOC',
  viewsonic: 'ViewSonic',
  acer: 'Acer',
  benq: 'BenQ',
  dell: 'Dell',
  hp: 'HP',
  lenovo: 'Lenovo',
  philips: 'Philips',
  innocn: 'INNOCN',
  xiaomi: 'Xiaomi',
  hkc: 'HKC',
};

const VALID_CATEGORIES: Category[] = ['focal', 'oled', 'gaming', 'graphic', 'curved', 'popular', 'portable'];
const VALID_STATUS: Status[] = ['active', 'discontinued', 'incoming'];

// ---- Value parsers ----

function normalizeBrand(s: string): string {
  const trimmed = s.trim();
  if (!trimmed) return '';
  return BRAND_ALIASES[trimmed.toLowerCase()] ?? trimmed;
}

function parseVietnameseNumber(s: string | number | undefined | null): number | null {
  if (s === null || s === undefined || s === '') return null;
  if (typeof s === 'number') return isNaN(s) ? null : s;
  const cleaned = String(s).replace(/\./g, '').replace(/[^\d,-]/g, '').replace(/,/g, '.').trim();
  if (!cleaned || cleaned === '-') return null;
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

function parseSize(s: string | number | undefined | null): number | null {
  if (s === null || s === undefined || s === '') return null;
  if (typeof s === 'number') return s;
  const m = String(s).match(/(\d+\.?\d*)/);
  return m ? parseFloat(m[1]!) : null;
}

function parseSizeFromName(name: string): number | null {
  const m = name.match(/\b(1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])\s*["”]/);
  return m ? parseFloat(m[1]!) : null;
}

function parseRefreshRate(s: string | number | undefined | null): number | null {
  if (s === null || s === undefined || s === '') return null;
  if (typeof s === 'number') return Math.round(s);
  const m = String(s).match(/(\d+)/);
  return m ? parseInt(m[1]!, 10) : null;
}

function parseHzFromName(name: string): number | null {
  const m = name.match(/(\d{2,3})\s*Hz/i);
  return m ? parseInt(m[1]!, 10) : null;
}

function normalizeResolution(s: string | undefined | null): Resolution | null {
  if (!s) return null;
  const upper = String(s).trim().toUpperCase();
  const map: Record<string, Resolution> = {
    FHD: 'FHD', '1080P': 'FHD', '1920X1080': 'FHD', '1920×1080': 'FHD',
    '2K': '2K', QHD: '2K', WQHD: '2K', '2560X1440': '2K', '2560×1440': '2K',
    '4K': '4K', UHD: '4K', '3840X2160': '4K', '3840×2160': '4K',
    UWQHD: 'UWQHD', '3440X1440': 'UWQHD', '3440×1440': 'UWQHD',
    '8K': '8K',
  };
  return map[upper] ?? null;
}

function parseResolutionFromName(name: string): Resolution {
  if (/UWQHD|3440\s*[xX×]\s*1440/i.test(name)) return 'UWQHD';
  if (/4K|UHD|3840/i.test(name)) return '4K';
  if (/2K|QHD|WQHD|2560/i.test(name)) return '2K';
  return 'FHD';
}

function parsePanelFromName(name: string): string {
  if (/QD-OLED/i.test(name)) return 'QD-OLED';
  if (/WOLED/i.test(name)) return 'WOLED';
  if (/OLED/i.test(name)) return 'OLED';
  if (/Nano\s+IPS/i.test(name)) return 'Nano IPS';
  if (/Fast\s+IPS/i.test(name)) return 'Fast IPS';
  if (/Mini.?LED/i.test(name)) return 'Mini-LED';
  if (/IPS/i.test(name)) return 'IPS';
  if (/VA/i.test(name)) return 'VA';
  if (/TN/i.test(name)) return 'TN';
  return 'IPS';
}

function parseCategoryFromName(name: string): Category {
  const nl = name.toLowerCase();
  if (/QD-OLED|WOLED|OLED/i.test(name)) return 'oled';
  if (/cong|curved/.test(nl)) return 'curved';
  if (/đồ họa|proart|color\s*accurate/.test(nl)) return 'graphic';
  if (/portable|di động|usb-?c.*portable/.test(nl)) return 'portable';
  if (/game|gaming|chuyên\s*game/.test(nl)) return 'gaming';
  return 'popular';
}

function parseTagsFromName(name: string): string[] {
  const tags: string[] = [];
  if (/USB.?C|USBC/i.test(name)) tags.push('usb-c');
  if (/G.?[Ss]ync/.test(name)) tags.push('gsync');
  if (/Free.?[Ss]ync/i.test(name)) tags.push('freesync');
  if (/HDR/i.test(name)) tags.push('hdr');
  if (/16:10/.test(name)) tags.push('16:10');
  return tags;
}

function parseFlag(s: unknown): 0 | 1 {
  if (typeof s === 'number') return s === 1 ? 1 : 0;
  const str = String(s ?? '').toLowerCase().trim();
  return ['1', 'true', 'yes', 'có', 'x', '✓', 'on'].includes(str) ? 1 : 0;
}

// ---- Main parser ----

export interface CsvParseResult {
  rows: ImportRow[];
  warnings: string[];
  errors: string[];
  headerMapping: Record<string, keyof ImportRow>;
  unmappedHeaders: string[];
}

function buildHeaderMapping(fields: string[]): {
  mapping: Record<string, keyof ImportRow>;
  unmapped: string[];
} {
  const mapping: Record<string, keyof ImportRow> = {};
  const unmapped: string[] = [];
  for (const h of fields) {
    const norm = h.toLowerCase().trim();
    const target = HEADER_MAP[norm];
    if (target) mapping[h] = target;
    else unmapped.push(h);
  }
  return { mapping, unmapped };
}

function transformRow(
  raw: Record<string, string>,
  mapping: Record<string, keyof ImportRow>,
  warnings: string[],
  rowNum: number
): ImportRow | null {
  const out: Record<string, unknown> = {};

  for (const [csvHeader, fieldName] of Object.entries(mapping)) {
    const v = raw[csvHeader];
    if (v === undefined || v === '' || v === null) continue;

    switch (fieldName) {
      case 'size':
        out.size = parseSize(v);
        break;
      case 'refresh_rate':
        out.refresh_rate = parseRefreshRate(v);
        break;
      case 'resolution': {
        const res = normalizeResolution(v);
        if (res) out.resolution = res;
        break;
      }
      case 'brand':
        out.brand = normalizeBrand(String(v));
        break;
      case 'price_msrp':
      case 'price_ct':
      case 'price_web':
      case 'discount_pct':
      case 'margin_vnd':
      case 'margin_pct':
        out[fieldName] = parseVietnameseNumber(v);
        break;
      case 'is_focal':
      case 'is_exclusive':
      case 'is_oled_focus':
      case 'is_branding':
        out[fieldName] = parseFlag(v);
        break;
      case 'category': {
        const cat = String(v).toLowerCase().trim();
        if (VALID_CATEGORIES.includes(cat as Category)) out.category = cat as Category;
        break;
      }
      case 'status': {
        const st = String(v).toLowerCase().trim();
        if (VALID_STATUS.includes(st as Status)) out.status = st as Status;
        break;
      }
      default:
        out[fieldName] = String(v).trim();
    }
  }

  if (!out.id) {
    warnings.push(`Dòng ${rowNum}: thiếu SKU/id, bỏ qua`);
    return null;
  }
  if (!out.name) {
    warnings.push(`Dòng ${rowNum} (${out.id}): thiếu tên sản phẩm, bỏ qua`);
    return null;
  }
  if (!out.brand) {
    warnings.push(`Dòng ${rowNum} (${out.id}): thiếu brand, bỏ qua`);
    return null;
  }

  // Auto-derive missing fields từ tên
  const name = String(out.name);
  if (out.size == null) {
    const auto = parseSizeFromName(name);
    if (auto != null) out.size = auto;
  }
  if (!out.resolution) out.resolution = parseResolutionFromName(name);
  if (out.refresh_rate == null) {
    const auto = parseHzFromName(name);
    if (auto != null) out.refresh_rate = auto;
  }
  if (!out.panel_type) out.panel_type = parsePanelFromName(name);
  if (!out.category) out.category = parseCategoryFromName(name);
  if (!out.tags) {
    const tags = parseTagsFromName(name);
    if (tags.length > 0) out.tags = tags.join(',');
  }
  if (!out.status) out.status = 'active';

  // Validate brand presence (warn nếu unknown)
  const brandLower = String(out.brand).toLowerCase();
  if (!Object.values(BRAND_ALIASES).map((b) => b.toLowerCase()).includes(brandLower)) {
    warnings.push(`Dòng ${rowNum} (${out.id}): brand "${out.brand}" không thuộc danh sách chuẩn`);
  }

  return out as unknown as ImportRow;
}

export function parseCSV(file: File): Promise<CsvParseResult> {
  return new Promise((resolve) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (result) => {
        const warnings: string[] = [];
        const errors: string[] = [];
        const fields = result.meta.fields ?? [];
        const { mapping, unmapped } = buildHeaderMapping(fields);

        // Required header check
        const mappedFields = new Set(Object.values(mapping));
        const missing: string[] = [];
        if (!mappedFields.has('id')) missing.push('SKU');
        if (!mappedFields.has('name')) missing.push('Tên sản phẩm');
        if (!mappedFields.has('brand')) missing.push('Hãng');

        if (missing.length > 0) {
          errors.push(`Thiếu cột bắt buộc: ${missing.join(', ')}. Header tìm thấy: ${fields.join(', ')}`);
          resolve({ rows: [], warnings, errors, headerMapping: mapping, unmappedHeaders: unmapped });
          return;
        }

        const rows: ImportRow[] = [];
        for (let i = 0; i < result.data.length; i++) {
          const raw = result.data[i]!;
          const row = transformRow(raw, mapping, warnings, i + 2); // +2 vì row 1 = header, count từ 1
          if (row) rows.push(row);
        }

        // Detect duplicates trong CSV
        const idSet = new Set<string>();
        const dupes = new Set<string>();
        for (const r of rows) {
          if (idSet.has(r.id)) dupes.add(r.id);
          idSet.add(r.id);
        }
        if (dupes.size > 0) {
          warnings.push(`Có SKU duplicate trong file: ${Array.from(dupes).join(', ')}`);
        }

        resolve({ rows, warnings, errors, headerMapping: mapping, unmappedHeaders: unmapped });
      },
      error: (err: Error) => {
        resolve({
          rows: [],
          warnings: [],
          errors: [`CSV parse failed: ${err.message}`],
          headerMapping: {},
          unmappedHeaders: [],
        });
      },
    });
  });
}
